import { z } from 'zod';

export const KPI_SATUAN_OPTIONS = [
  { label: 'Rupiah', value: 'rupiah' },
  { label: 'Tanggal', value: 'tanggal' },
  { label: 'Persen', value: 'persen' },
  { label: 'Volume', value: 'volume' },
] as const;

export const KPI_ARAH_OPTIONS = [
  { label: 'Lebih Besar Lebih Baik', value: 'lebih besar lebih baik' },
  { label: 'Lebih Kecil Lebih Baik', value: 'lebih kecil lebih baik' },
] as const;

export const kpiItemSchema = z.object({
  id: z.number().optional(),
  org_job_description_id: z.number().min(1, 'Report wajib dipilih'),
  satuan: z.enum(['rupiah', 'tanggal', 'persen', 'volume'], { required_error: 'Satuan wajib dipilih' }),
  target_kpi: z.string().min(1, 'Angka target wajib diisi'),
  definisi_target: z.string().min(1, 'Definisi target wajib diisi'),
  target: z.union([z.number().min(0, 'Target harus ≥ 0'), z.string()]),
  realisasi: z.union([z.number().min(0, 'Realisasi harus ≥ 0'), z.string()]),
  bobot: z
    .number({
      required_error: 'Bobot tidak boleh 0',
      invalid_type_error: 'Bobot tidak boleh 0',
    })
    .min(1, 'Bobot tidak boleh 0')
    .max(100, 'Bobot maksimal 100'),
  arah: z.enum(['lebih besar lebih baik', 'lebih kecil lebih baik']).default('lebih besar lebih baik'),
});

/**
 * Maximum allowed sum of bobot across all KPI items in a single form.
 * Mirrors the business rule "Jumlah total dari % bobot maksimal 100%".
 */
export const KPI_MAX_TOTAL_BOBOT = 100;

/**
 * Frekuensi jobdesc yang tidak diukur lewat KPI. Jobdesc dengan frekuensi ini
 * tetap muncul di menu Report supaya pengguna masih bisa mengunggah form
 * laporannya — yang dilewati hanya pengukuran KPI-nya.
 */
export const KPI_EXCLUDED_FREQUENCIES = ['Tahunan', 'Insidentil'] as const;

/**
 * Returns true when a job description's frequency makes it eligible as a KPI
 * row. A jobdesc with no frequency recorded stays eligible: the rule only
 * excludes the two frequencies named above, and older rows predate the field.
 */
export const isKpiEligibleFrequency = (frequency?: string | null): boolean => {
  const value = frequency?.trim().toLowerCase();
  if (!value) return true;
  return !KPI_EXCLUDED_FREQUENCIES.some(excluded => excluded.toLowerCase() === value);
};

/**
 * Returns the sum of bobot across the supplied items, treating non-numeric
 * or negative values as zero. Exported so the form UI can mirror the same
 * computation when showing the running total and disabling submit.
 */
export const sumKpiBobot = (items: Array<{ bobot?: number | null }> | undefined): number => {
  if (!items?.length) return 0;
  return items.reduce((acc, it) => {
    const n = Number(it?.bobot);
    return acc + (Number.isFinite(n) && n > 0 ? n : 0);
  }, 0);
};

export const kpiFormSchema = z
  .object({
    org_chart_node_id: z.number().min(1, 'Jabatan wajib dipilih'),
    items: z.array(kpiItemSchema).min(1, 'Minimal 1 KPI wajib diisi'),
  })
  .superRefine((value, ctx) => {
    const total = sumKpiBobot(value.items);
    if (total > KPI_MAX_TOTAL_BOBOT) {
      // Attach the issue to `items` so react-hook-form surfaces it as a
      // form-level error users can see above the rows, AND record the
      // exact overflow value in the message.
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['items'],
        message: `Total % bobot adalah ${total}%, melebihi batas ${KPI_MAX_TOTAL_BOBOT}%. Kurangi salah satu nilai bobot.`,
      });
    }
  });

export type KpiItemValues = z.infer<typeof kpiItemSchema>;
export type KpiFormValues = z.infer<typeof kpiFormSchema>;

export const defaultKpiItem = (): KpiItemValues => ({
  org_job_description_id: 0,
  satuan: 'volume',
  target_kpi: '0',
  definisi_target: '',
  target: 0,
  realisasi: 0,
  bobot: 0,
  arah: 'lebih besar lebih baik',
});

/**
 * Reduces any input to its bare digits and a single optional decimal dot.
 * E.g., "1.000,50" -> "1000.50". It strips PUEBI thousands dots and converts decimal comma to dot.
 */
export const stripThousandSeparator = (value: string): string => {
  const v = (value ?? '').toString();
  // Allow digits, dots (thousands) and commas (decimals)
  const digitsAndCommas = v.replace(/[^\d,]/g, '');
  const parts = digitsAndCommas.split(',');
  if (parts.length > 1) {
    const integerPart = parts[0].replace(/^0+(?=\d)/, '');
    const decimalPart = parts.slice(1).join('');
    return `${integerPart || '0'}.${decimalPart}`;
  }
  return parts[0].replace(/^0+(?=\d)/, '');
};

/**
 * Formats a string (which represents a JS number/float, e.g. "1000.50") into PUEBI
 * format, e.g. "1.000,50".
 */
export const formatThousandSeparator = (value: string | number): string => {
  const v = value == null ? '' : String(value);
  const cleanValue = v.replace(/[^\d.]/g, '');
  if (cleanValue === '') return '';

  const parts = cleanValue.split('.');
  const integerPart = parts[0].replace(/^0+(?=\d)/, '') || '0';
  const decimalPart = parts.length > 1 ? parts[1] : null;

  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');

  if (decimalPart !== null) {
    return `${formattedInteger},${decimalPart}`;
  }
  return formattedInteger;
};

export const formatPuebi = (value: number, fractionDigits = 1): string => {
  if (isNaN(value)) return '0';
  return new Intl.NumberFormat('id-ID', {
    minimumFractionDigits: 0,
    maximumFractionDigits: fractionDigits,
  }).format(value);
};

export const getDefaultTargetKpi = (satuan: KpiItemValues['satuan']): string => {
  if (satuan === 'tanggal') return new Date().toISOString().split('T')[0];
  return '0';
};

export const getDefaultTargetValue = (satuan: KpiItemValues['satuan']): number | string => {
  if (satuan === 'tanggal') return new Date().toISOString().split('T')[0];
  return 0;
};

/**
 * Calculates pencapaian (achievement percentage).
 * For 'tanggal' satuan: 100 if realisasi date is on or before target date.
 *   When late, a graduated penalty applies: 100 - (daysLate * 10), floored at 0.
 *   So 1 day late = 90%, 5 days late = 50%, 10+ days late = 0%.
 * For numeric satuan with 'lebih besar lebih baik': returns (realisasi / target) * 100, capped at 100.
 * For numeric satuan with 'lebih kecil lebih baik': returns (target / realisasi) * 100.
 */
export function calcPencapaian(
  satuan: KpiItemValues['satuan'],
  target: number | string,
  realisasi: number | string,
  arah: KpiItemValues['arah'] = 'lebih besar lebih baik',
): number {
  if (satuan === 'tanggal') {
    if (!target || !realisasi) return 0;
    const targetDate = new Date(String(target));
    const realisasiDate = new Date(String(realisasi));
    if (isNaN(targetDate.getTime()) || isNaN(realisasiDate.getTime())) return 0;
    if (realisasiDate <= targetDate) return 100;
    const daysLate = Math.round((realisasiDate.getTime() - targetDate.getTime()) / 86_400_000);
    return Math.max(0, 100 - daysLate * 10);
  }
  const t = Number(target) || 0;
  const r = Number(realisasi) || 0;
  if (arah === 'lebih kecil lebih baik') {
    return r > 0 ? (t / r) * 100 : 0;
  }
  return t > 0 ? Math.min(100, (r / t) * 100) : 0;
}
